import type React from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type { SelectOption } from '@/_components';
import type { StateQuerySchema } from '@/_schemas/generic';

import type { EnteredAddressSnapshot } from './components/validation-modal';
import type { AddressType } from './model';
import type {
  AddressFormSchema,
  AddressQuerySchema,
  AddressValidationRequest,
  AddressValidationResponse,
  RecommendedAction,
  ValidationDecision,
} from './shared';

import type { CountryQueryLinkerSchema } from '../../../_schemas/countries';
import type { SectionHeadingProps } from '../../headers';

export type OnAddressChange = (t: 'UPDATE' | 'DELETE' | 'CREATE' | 'DELETED', v: AddressQuerySchema[]) => void;

export type AddressProps = Omit<SectionHeadingProps, 'rightSlot'> & {
  type: AddressType;
  value: AddressQuerySchema | undefined;
  isEdit: boolean;
  onRequestEdit: () => void;
  disabled?: boolean;
  noLayout?: boolean;
  nameKey?: string;
  onChange: (value: AddressQuerySchema[]) => void;
  protectedIds?: number[];
};

export type AddressForm = UseFormReturn<AddressFormSchema>;

export type PendingAddressUpdate = {
  applyRecommended?: boolean;
  keepCurrent?: boolean;
  unitNumber?: string;
};

export type AddressFormHandle = {
  getFormData: () => { request: AddressValidationRequest; snapshot: EnteredAddressSnapshot };
  applyRecommended: () => void;
  applyUnit: (unit: string) => void;
  markValidationDirty: () => void;
};

export type AddressValidationState = {
  validationAction: RecommendedAction | null;
  validationDecision: ValidationDecision;
  validationResult: AddressValidationResponse | null;
  onResetValidation: () => void;
  pendingUpdateRef: React.MutableRefObject<PendingAddressUpdate | null>;
  setCanValidateRef: React.MutableRefObject<((v: boolean) => void) | null>;
};

export type AddressFormProps = Omit<AddressProps, 'text' | 'icon'> & {
  validationState?: AddressValidationState;
};

export type AddressSectionLayoutProps = Pick<AddressProps, 'isEdit' | 'text' | 'icon' | 'noLayout' | 'value'> & {
  children: React.ReactNode;
  canValidate?: boolean;
  isValidatingAddress?: boolean;
  isValidated?: boolean;
  showUpdateReminder?: boolean;
  onValidateAddress?: () => void | Promise<void>;
};

export type AddressFieldsProps = {
  form: AddressForm;
  isEdit: boolean;
  isLookingUpPostalCode?: boolean;
  cityOptions: SelectOption[];
  stateOptions: StateQuerySchema[];
  countriesOptions: CountryQueryLinkerSchema[];
  showCityAsInput?: boolean;
  showCountryAsInput?: boolean;
  showRegion?: boolean;
};

export type AddressPreviewProps = Pick<AddressProps, 'value'>;

export const addressesIncludeKey = (v = '', overrideKey = false) => {
  const key = v ? `${v}.` : '';
  if (overrideKey) {
    return `${key}state,${key}country`;
  }
  return `${key}addresses.state,${key}addresses.country`;
};
